<template>
    <div>
        <div class="container-fluid">
            <div class="form-row report-header">
                <div class="col-12 text-center">
                    <h5>Importar Vuelos desde Excel</h5>
                </div>
            </div>

            <div class="form-row align-items-end mb-2 mt-4">
                <div class="col-md-1">
                    <label for="year">Año</label>
                    <input v-model.number="year" type="number" class="form-control" id="year">
                </div>
                <div class="col-md-2">
                    <label for="month">Mes</label>
                    <select v-model.number="month" class="form-control" id="month">
                        <option v-for="m in months" :key="m.value" :value="m.value">{{m.label}}</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label for="status">Estado (vuelo)</label>
                    <select v-model="status" class="form-control" id="status">
                        <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{s.label}}</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label for="legConfig">Configuración (legs)</label>
                    <select v-model="defaultSalesConfigId" class="form-control" id="legConfig">
                        <option :value="null">-- Sin configuración --</option>
                        <option v-for="o in salesConfigOptions" :key="o.value" :value="o.value">{{o.label}}</option>
                    </select>
                </div>
                <div class="col-md-2">
                    <label for="legStatus">Estado (legs)</label>
                    <select v-model="defaultLegStatus" class="form-control" id="legStatus">
                        <option v-for="o in legStatusOptions" :key="o.value" :value="o.value">{{o.label}}</option>
                    </select>
                </div>
                <div class="col-md-3">
                    <button type="button" class="btn btn-primary" @click="openFile">
                        <font-awesome-icon icon="file-excel"/> Cargar Excel
                    </button>
                    <input id="import-flights-file" type="file" accept=".xlsx,.xls"
                        style="display: none;" @change="onFileChange" />
                </div>
            </div>
            <div class="form-row mb-2" v-if="fileName">
                <div class="col-12"><small class="text-muted">Archivo: {{fileName}}</small></div>
            </div>

            <div v-if="processing" class="text-center my-4">
                <img class="loading-gif" src="@/img/loading.gif">
            </div>

            <div v-if="!processing && flights.length">
                <div class="alert alert-info d-flex justify-content-between align-items-center flex-wrap">
                    <span>
                        <strong>{{flights.length}}</strong> vuelos ·
                        <strong>{{totalLegs}}</strong> segmentos ·
                        <strong>{{daysCount}}</strong> días
                        <span v-if="savedOk"> · <span class="text-success">{{savedOk}} grabados</span></span>
                        <span v-if="savedError"> · <span class="text-danger">{{savedError}} con error</span></span>
                    </span>
                    <span>
                        <button type="button" class="btn btn-success btn-sm" @click="saveAll" :disabled="saving">
                            <font-awesome-icon icon="save"/> Grabar Todos
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="downloadJson" :disabled="saving">
                            <font-awesome-icon icon="download"/> Descargar JSON
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="copyJson" :disabled="saving">
                            <font-awesome-icon icon="copy"/> Copiar JSON
                        </button>
                    </span>
                </div>

                <div v-if="saving" class="progress mb-2">
                    <div class="progress-bar" role="progressbar"
                        :style="{width: savePct + '%'}">{{saveProgress}} / {{flights.length}}</div>
                </div>

                <div v-if="warnings.length" class="alert alert-warning">
                    <div v-for="(w, wi) in warnings" :key="wi">{{w}}</div>
                </div>

                <div v-if="ignored.length" class="alert alert-warning">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong>
                            {{ignored.length}} fila(s) con vuelos ignorados
                            ({{ignoredLegsCount}} segmentos) — no se pudo determinar el día del mes
                        </strong>
                        <button type="button" class="btn btn-outline-dark btn-sm" @click="showIgnored = !showIgnored">
                            {{showIgnored ? 'Ocultar detalle' : 'Ver detalle'}}
                        </button>
                    </div>
                    <div v-if="showIgnored" class="table-responsive">
                        <table class="table table-sm table-bordered mb-0 bg-white">
                            <thead>
                                <tr>
                                    <th>Fila Excel</th>
                                    <th>Matrícula</th>
                                    <th>Vuelos ignorados (segmentos)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ig, ii) in ignored" :key="ii">
                                    <td>{{ig.row}}</td>
                                    <td>{{ig.mat}}</td>
                                    <td>
                                        <span v-for="(leg, li) in ig.legs" :key="li" class="ignored-leg">
                                            #{{leg.fnum}} {{leg.route}} ({{leg.std}}→{{leg.sta}})
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="table table-striped table-sm table-bordered align-middle">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Vuelo</th>
                                <th>Día</th>
                                <th>Estado</th>
                                <th>Ruta</th>
                                <th>STD</th>
                                <th>STA</th>
                                <th>Configuración</th>
                                <th>Estado leg</th>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(f, i) in flights" :key="i" :class="rowClass(i)">
                                <td>{{f.FlightDate}}</td>
                                <td>{{f.FlightNumber}}</td>
                                <td>{{weekDayLabel(f.WeekDay)}}</td>
                                <td>
                                    <select v-model="f.Status" class="form-control form-control-sm">
                                        <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{s.label}}</option>
                                    </select>
                                </td>
                                <td>
                                    <div v-for="(leg, li) in f.InventoryLegs" :key="li" class="cell-line">
                                        {{leg.DepartureStation}} → {{leg.ArrivalStation}}
                                    </div>
                                </td>
                                <td>
                                    <div v-for="(leg, li) in f.InventoryLegs" :key="li" class="cell-line">{{leg.STDTime}}</div>
                                </td>
                                <td>
                                    <div v-for="(leg, li) in f.InventoryLegs" :key="li" class="cell-line">{{leg.STATime}}</div>
                                </td>
                                <td>
                                    <div v-for="(leg, li) in f.InventoryLegs" :key="li" class="cell-line">
                                        <select v-model="leg.SalesConfigurationId" class="form-control form-control-sm">
                                            <option :value="null">--</option>
                                            <option v-for="o in salesConfigOptions" :key="o.value" :value="o.value">{{o.label}}</option>
                                        </select>
                                    </div>
                                </td>
                                <td>
                                    <div v-for="(leg, li) in f.InventoryLegs" :key="li" class="cell-line">
                                        <select v-model="leg.LegStatus" class="form-control form-control-sm">
                                            <option v-for="o in legStatusOptions" :key="o.value" :value="o.value">{{o.label}}</option>
                                        </select>
                                    </div>
                                </td>
                                <td class="text-nowrap">
                                    <button type="button" class="btn btn-primary btn-sm" @click="saveOne(i)"
                                        :disabled="saving || rowState[i].status==='saving'">
                                        Grabar
                                    </button>
                                    <div v-if="rowState[i].status==='ok'" class="text-success small">
                                        <font-awesome-icon icon="save"/> OK
                                    </div>
                                    <div v-if="rowState[i].status==='error'" class="text-danger small" :title="rowState[i].message">
                                        Error
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="!processing && loaded && !flights.length" class="alert alert-danger">
                No se encontraron vuelos en el archivo. Verifique el formato y el mes/año seleccionados.
            </div>
        </div>
    </div>
</template>

<script>
import '@/css/app.css';
import XLSX from 'xlsx';

const serverurl = process.env.SERVER_ENDPOINT;

const MONTHS = {
    'ENERO': 1, 'FEBRERO': 2, 'MARZO': 3, 'ABRIL': 4, 'MAYO': 5, 'JUNIO': 6,
    'JULIO': 7, 'AGOSTO': 8, 'SEPTIEMBRE': 9, 'SETIEMBRE': 9, 'OCTUBRE': 10,
    'NOVIEMBRE': 11, 'DICIEMBRE': 12
};
// Cada bloque de segmento ocupa 5 columnas: Orig, Dep, NroVuelo, Arr, Dest.
// Los bloques comienzan en estas columnas (0-based), separados por 1 columna de conexión.
const BLOCK_COLS = [3, 9, 15, 21, 27, 33];
const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export default {
    name: 'import-flights',
    data () {
        return {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            status: 'CONFIRMED',
            defaultSalesConfigId: null,
            defaultLegStatus: 'OPEN',
            salesConfigOptions: [],
            statusOptions: [
                {value: 'CONFIRMED', label: 'Confirmado'},
                {value: 'SUSPENDED', label: 'Cerrado a la Venta'},
                {value: 'CANCELED', label: 'Cancelado'},
            ],
            legStatusOptions: [
                {value: 'OPEN', label: 'Abierto'},
                {value: 'CLOSE_PENDING', label: 'Cierre Pendiente'},
                {value: 'CANCELED', label: 'Cancelado'},
                {value: 'CLOSED', label: 'Cerrado'},
            ],
            months: [
                {value: 1, label: 'Enero'}, {value: 2, label: 'Febrero'},
                {value: 3, label: 'Marzo'}, {value: 4, label: 'Abril'},
                {value: 5, label: 'Mayo'}, {value: 6, label: 'Junio'},
                {value: 7, label: 'Julio'}, {value: 8, label: 'Agosto'},
                {value: 9, label: 'Septiembre'}, {value: 10, label: 'Octubre'},
                {value: 11, label: 'Noviembre'}, {value: 12, label: 'Diciembre'},
            ],
            flights: [],
            rowState: [],
            warnings: [],
            ignored: [],
            showIgnored: false,
            fileName: null,
            processing: false,
            loaded: false,
            lastRows: null,
            saving: false,
            saveProgress: 0,
        }
    },
    computed: {
        totalLegs () {
            return this.flights.reduce((a, f) => a + f.InventoryLegs.length, 0);
        },
        daysCount () {
            let days = {};
            for (let f of this.flights) days[f.FlightDate] = true;
            return Object.keys(days).length;
        },
        jsonText () {
            return JSON.stringify(this.flights, null, 4);
        },
        savedOk () {
            return this.rowState.filter(r => r.status === 'ok').length;
        },
        savedError () {
            return this.rowState.filter(r => r.status === 'error').length;
        },
        savePct () {
            if (!this.flights.length) return 0;
            return Math.round(this.saveProgress / this.flights.length * 100);
        },
        ignoredLegsCount () {
            return this.ignored.reduce((a, ig) => a + ig.legs.length, 0);
        }
    },
    async mounted () {
        await this.loadSalesConfigurations();
    },
    methods: {
        async loadSalesConfigurations () {
            try {
                let resp = await fetch(serverurl + '/api/salesconfiguration/', {credentials: 'include'});
                if (resp.ok) {
                    let list = await resp.json();
                    this.salesConfigOptions = list.map(r => ({value: r.id, label: r.Name}));
                }
            } catch (e) {
                console.log(e);
            }
        },
        openFile () {
            let input = document.getElementById('import-flights-file');
            input.value = null;
            input.click();
        },
        onFileChange (e) {
            let file = e.target.files[0];
            if (!file) return;
            this.fileName = file.name;
            this.processing = true;
            this.loaded = false;
            let self = this;
            let reader = new FileReader();
            reader.onload = function (ev) {
                try {
                    let workbook = XLSX.read(ev.target.result, {type: 'binary'});
                    let sheetName = workbook.SheetNames[0];
                    let sheet = workbook.Sheets[sheetName];
                    self.lastRows = self.sheetToRows(sheet);
                    self.detectPeriod(self.lastRows, sheetName);
                    self.build();
                } catch (err) {
                    console.log(err);
                    alert('No se pudo leer el archivo Excel');
                } finally {
                    self.processing = false;
                    self.loaded = true;
                }
            };
            reader.onerror = function (ex) {
                console.log(ex);
                self.processing = false;
                alert('Error al leer el archivo');
            };
            reader.readAsBinaryString(file);
        },
        // Convierte la hoja a filas propagando las celdas combinadas de la
        // columna A (donde está el día del mes; a veces es una celda combinada
        // que abarca varias filas del bloque del día).
        sheetToRows (sheet) {
            let rows = XLSX.utils.sheet_to_json(sheet, {
                header: 1, raw: true, defval: null, blankrows: true
            });
            let merges = sheet['!merges'] || [];
            for (let m of merges) {
                if (m.s.c !== 0) continue;
                let val = (rows[m.s.r] || [])[0];
                for (let r = m.s.r; r <= m.e.r; r++) {
                    if (!rows[r]) rows[r] = [];
                    rows[r][0] = val;
                }
            }
            return rows;
        },
        detectPeriod (rows, sheetName) {
            let sources = [];
            for (let i = 0; i < 5 && i < rows.length; i++) {
                let a = this.norm(rows[i] && rows[i][0]);
                if (a) sources.push(a);
            }
            if (sheetName) sources.push(this.norm(sheetName));
            if (this.fileName) sources.push(this.norm(this.fileName));
            for (let text of sources) {
                let foundMonth = null, foundYear = null;
                for (let m in MONTHS) {
                    if (text.indexOf(m) >= 0) foundMonth = MONTHS[m];
                }
                let y4 = text.match(/(20\d\d)/);
                if (y4) {
                    foundYear = Number(y4[1]);
                } else if (foundMonth) {
                    let y2 = text.match(/\b(\d{2})\b/);
                    if (y2) foundYear = 2000 + Number(y2[1]);
                }
                if (foundMonth) this.month = foundMonth;
                if (foundYear) this.year = foundYear;
                if (foundMonth && foundYear) break;
            }
        },
        build () {
            if (!this.lastRows) return;
            let res = this.parse(this.lastRows, this.year, this.month);
            this.flights = res.flights;
            this.warnings = res.warnings;
            this.ignored = res.ignored;
            this.rowState = this.flights.map(() => ({status: '', message: ''}));
        },
        parse (rows, year, month) {
            let warnings = [];
            let ignored = [];
            let n = rows.length;
            // Día del mes por fila: número en la columna A (celdas combinadas ya
            // propagadas por sheetToRows).
            let dayOfRow = new Array(n).fill(null);
            for (let i = 0; i < n; i++) {
                let a = rows[i] ? rows[i][0] : null;
                if (this.isDayNumber(a)) dayOfRow[i] = Number(a);
            }
            // Las filas sin número (por ejemplo la primera rotación del día, que
            // suele ir ARRIBA del número del día) se asignan al número de día más
            // cercano hacia ABAJO.
            let assignedDay = new Array(n).fill(null);
            let cur = null;
            for (let i = n - 1; i >= 0; i--) {
                if (dayOfRow[i] != null) cur = dayOfRow[i];
                assignedDay[i] = cur;
            }

            let flights = [];
            for (let i = 0; i < n; i++) {
                let row = rows[i] || [];
                let day = assignedDay[i];
                let raw = this.parseRowLegs(row);
                if (!raw.length) continue;
                if (!day) {
                    ignored.push({
                        row: i + 1,
                        mat: (row[1] == null ? '' : String(row[1]).trim()),
                        legs: raw.map(leg => ({
                            fnum: leg.fnum,
                            route: leg.orig + '→' + leg.dest,
                            std: leg.depMin != null ? this.hhmm(leg.depMin) : '--:--',
                            sta: leg.arrMin != null ? this.hhmm(leg.arrMin) : '--:--',
                        })),
                    });
                    continue;
                }
                let base = new Date(year, month - 1, day);
                let curFlight = null, dayOffset = 0, prevDep = -1;
                for (let leg of raw) {
                    if (leg.depMin == null || leg.arrMin == null) continue;
                    if (prevDep >= 0 && leg.depMin < prevDep) dayOffset++;
                    prevDep = leg.depMin;
                    let legDate = this.addDays(base, dayOffset);
                    let staDate = this.addDays(base, dayOffset + (leg.arrMin < leg.depMin ? 1 : 0));
                    let legObj = {
                        ArrivalStation: leg.dest,
                        DepartureDate: this.fmtDate(legDate),
                        DepartureStation: leg.orig,
                        FlightNumber: leg.fnum,
                        LegStatus: this.defaultLegStatus,
                        SalesConfigurationId: this.defaultSalesConfigId,
                        STA: this.fmtDate(staDate) + 'T' + this.hhmm(leg.arrMin) + ':00',
                        STATime: this.hhmm(leg.arrMin),
                        STD: this.fmtDate(legDate) + 'T' + this.hhmm(leg.depMin) + ':00',
                        STDTime: this.hhmm(leg.depMin),
                    };
                    if (curFlight && curFlight.FlightNumber === leg.fnum) {
                        curFlight.InventoryLegs.push(legObj);
                    } else {
                        curFlight = {
                            FlightDate: this.fmtDate(legDate),
                            FlightNumber: leg.fnum,
                            InventoryLegs: [legObj],
                            Status: this.status,
                            WeekDay: this.weekDay(legDate),
                        };
                        flights.push(curFlight);
                    }
                }
            }
            return {flights, warnings, ignored};
        },
        parseRowLegs (row) {
            let legs = [];
            for (let bc of BLOCK_COLS) {
                let orig = row[bc], dep = row[bc + 1], fnum = row[bc + 2],
                    arr = row[bc + 3], dest = row[bc + 4];
                if (this.isAirport(orig) && this.isAirport(dest) && this.isFlightNum(fnum)) {
                    legs.push({
                        orig: String(orig).trim().toUpperCase(),
                        dest: String(dest).trim().toUpperCase(),
                        fnum: String(Number(fnum)),
                        depMin: this.toMinutes(dep),
                        arrMin: this.toMinutes(arr),
                    });
                }
            }
            return legs;
        },
        norm (s) { return (s == null ? '' : String(s)).trim().toUpperCase(); },
        isDayNumber (s) {
            if (s == null || s === '') return false;
            let n = Number(s);
            return Number.isInteger(n) && n >= 1 && n <= 31;
        },
        isAirport (s) { return typeof s === 'string' && /^[A-Za-z]{3}$/.test(s.trim()); },
        isFlightNum (v) {
            if (v == null || v === '') return false;
            let n = Number(v);
            return Number.isInteger(n) && n >= 1 && n < 100000;
        },
        toMinutes (frac) {
            if (frac == null || frac === '') return null;
            let f = Number(frac);
            if (isNaN(f)) return null;
            f = f - Math.floor(f);
            return Math.round(f * 1440);
        },
        pad (n) { return n < 10 ? '0' + n : '' + n; },
        hhmm (min) {
            min = ((min % 1440) + 1440) % 1440;
            return this.pad(Math.floor(min / 60)) + ':' + this.pad(min % 60);
        },
        fmtDate (d) {
            return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
        },
        addDays (d, n) {
            let r = new Date(d.getTime());
            r.setDate(r.getDate() + n);
            return r;
        },
        weekDay (d) { return (d.getDay() + 6) % 7; },
        weekDayLabel (wd) { return WEEKDAY_LABELS[wd] != null ? WEEKDAY_LABELS[wd] : wd; },
        rowClass (i) {
            let s = this.rowState[i] && this.rowState[i].status;
            if (s === 'ok') return 'table-success';
            if (s === 'error') return 'table-danger';
            return '';
        },
        // Graba un vuelo contra el mismo endpoint que usa el editor de vuelos.
        postFlight (flight) {
            return fetch(serverurl + '/api/flight/', {
                method: 'POST',
                mode: 'cors',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(flight)
            }).then(async (resp) => {
                if (resp.ok) {
                    let result = await resp.json();
                    return {ok: true, result: result};
                }
                if (resp.status == 401) {
                    window.location.replace(window.location.origin + '/#/login');
                    return {ok: false, message: 'Sesión expirada'};
                }
                let msg = await resp.text();
                return {ok: false, message: msg || ('Error ' + resp.status)};
            }).catch((e) => ({ok: false, message: String(e)}));
        },
        async saveOne (i) {
            this.rowState[i].status = 'saving';
            this.rowState[i].message = '';
            this.rowState = this.rowState.slice();
            let res = await this.postFlight(this.flights[i]);
            this.rowState[i].status = res.ok ? 'ok' : 'error';
            this.rowState[i].message = res.ok ? '' : res.message;
            this.rowState = this.rowState.slice();
            return res.ok;
        },
        async saveAll () {
            if (this.saving) return;
            if (!confirm('¿Grabar ' + this.flights.length + ' vuelos?')) return;
            this.saving = true;
            this.saveProgress = 0;
            for (let i = 0; i < this.flights.length; i++) {
                if (this.rowState[i].status === 'ok') { this.saveProgress++; continue; }
                await this.saveOne(i);
                this.saveProgress++;
            }
            this.saving = false;
            alert('Proceso finalizado. Grabados: ' + this.savedOk + ' · Con error: ' + this.savedError);
        },
        downloadJson () {
            let blob = new Blob([this.jsonText], {type: 'application/json'});
            let url = URL.createObjectURL(blob);
            let a = document.createElement('a');
            a.href = url;
            a.download = 'vuelos_' + this.year + '_' + this.pad(this.month) + '.json';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },
        copyJson () {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(this.jsonText).then(function () {
                    alert('JSON copiado al portapapeles');
                });
            } else {
                let ta = document.createElement('textarea');
                ta.value = this.jsonText;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                alert('JSON copiado al portapapeles');
            }
        },
    },
    watch: {
        year () { if (this.lastRows) this.build(); },
        month () { if (this.lastRows) this.build(); },
        status () { if (this.lastRows) this.build(); },
        defaultSalesConfigId () { if (this.lastRows) this.build(); },
        defaultLegStatus () { if (this.lastRows) this.build(); },
    },
}
</script>

<style scoped>
.cell-line {
    padding: 1px 0;
}
.cell-line + .cell-line {
    border-top: 1px dashed #dee2e6;
}
.form-control-sm {
    min-width: 90px;
}
.ignored-leg {
    display: inline-block;
    background: #fff3cd;
    border: 1px solid #ffe69c;
    border-radius: 4px;
    padding: 1px 6px;
    margin: 2px;
    font-size: 12px;
    white-space: nowrap;
}
</style>
